import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LIST_LIMIT_MAX } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader, KpiGrid, KpiCard, Toolbar } from '../ui/Page';
import { UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { StatusBadge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { money, moneyShort } from '../money';
import { IconPipeline } from '../icons';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';

type Quote = {
  id: string;
  quoteNumber: number | null;
  customerId: string;
  title: string | null;
  status: string;
  totalCents: number;
  customerName: string | null;
  customerKind: string | null;
};
type Job = {
  id: string;
  jobNumber: number;
  customerId: string;
  customerName: string | null;
  customerKind: string | null;
  title: string;
  status: string;
};

/** Card as it appears on the board — quotes and jobs normalized into one shape. */
type Card = {
  id: string;
  kind: 'quote' | 'job';
  ref: string;
  title: string;
  customerId: string;
  /** Both joined by the server — the board used to download every customer to read them. */
  customerName: string | null;
  customerKind: string | null;
  valueCents: number | null;
  stage: string;
};

/**
 * The pipeline board (PRD v2 §4): every VALIDATED item, quote stages flowing into job
 * stages. Requests are deliberately absent — they live behind the gate on /requests.
 * Pipelines are label-defined; residential vs commercial reads the customer's kind.
 */
const STAGES: { key: string; label: string; match: (c: Card) => boolean }[] = [
  {
    key: 'quoting',
    label: 'Quoting',
    match: (c) => c.kind === 'quote' && (c.stage === 'draft' || c.stage === 'sent'),
  },
  {
    key: 'won',
    label: 'Won — to schedule',
    match: (c) =>
      (c.kind === 'quote' && c.stage === 'approved') ||
      (c.kind === 'job' && c.stage === 'unscheduled'),
  },
  {
    key: 'scheduled',
    label: 'Scheduled',
    match: (c) => c.kind === 'job' && (c.stage === 'scheduled' || c.stage === 'on_hold'),
  },
  {
    key: 'working',
    label: 'In progress',
    match: (c) => c.kind === 'job' && c.stage === 'in_progress',
  },
  {
    key: 'toinvoice',
    label: 'Ready to invoice',
    match: (c) => c.kind === 'job' && c.stage === 'work_done',
  },
];

const PIPELINES = [
  { key: 'all', label: 'All work' },
  { key: 'residential', label: 'Residential' },
  { key: 'commercial', label: 'Commercial' },
];

const BOARD_PAGE = { limit: LIST_LIMIT_MAX, offset: 0 };

export function PipelinePage() {
  const trpc = useTRPC();
  const search = useSearch({ strict: false });
  const q = useQuery(trpc.jobPipeline.list.queryOptions());
  const state = shownState(q, q.data);
  if (state !== 'ready')
    return (
      <PageRoot>
        <PageHeader title="Pipeline" />
        <UnresolvedState
          state={state}
          what="your pipeline settings"
          icon={IconPipeline}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      </PageRoot>
    );
  if (q.data?.enabled)
    return <Navigate to="/jobs" search={{ ...search, view: 'pipeline' }} replace />;
  return (
    <>
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      <LegacyPipelinePage />
    </>
  );
}

function LegacyPipelinePage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  // A board, not a table: it shows cards rather than paging, so it asks for the largest
  // page the contract allows and says so below when there is more than that. A shop with
  // 200+ live quotes and jobs has outgrown a board, not just this query.
  const quotes = useQuery(trpc.quote.list.queryOptions(BOARD_PAGE));
  const jobs = useQuery(trpc.job.list.queryOptions(BOARD_PAGE));
  const [pipeline, setPipeline] = useState('all');

  if (quotes.isPending || jobs.isPending) return <PageSkeleton />;
  if (quotes.isError || jobs.isError) {
    return (
      <PageRoot>
        <PageHeader title="Pipeline" />
        <ErrorState
          icon={IconPipeline}
          what="your pipeline"
          onRetry={() => {
            void quotes.refetch();
            void jobs.refetch();
          }}
        />
      </PageRoot>
    );
  }

  const truncatedBy =
    Math.max(0, quotes.data.total - quotes.data.rows.length) +
    Math.max(0, jobs.data.total - jobs.data.rows.length);

  const cards: Card[] = [
    ...(quotes.data.rows as Quote[]).map((q) => ({
      id: q.id,
      kind: 'quote' as const,
      ref: q.quoteNumber ? `Q-${q.quoteNumber}` : 'Draft',
      title: q.title ?? 'Untitled quote',
      customerId: q.customerId,
      customerName: q.customerName,
      customerKind: q.customerKind,
      valueCents: q.totalCents,
      stage: q.status,
    })),
    ...(jobs.data.rows as Job[]).map((j) => ({
      id: j.id,
      kind: 'job' as const,
      ref: `#${j.jobNumber}`,
      title: j.title,
      customerId: j.customerId,
      customerName: j.customerName,
      customerKind: j.customerKind,
      valueCents: null, // job value arrives with job costing (V1 tail)
      stage: j.status,
    })),
  ].filter((c) => {
    if (pipeline === 'all') return true;
    return c.customerKind === pipeline;
  });

  const columns = STAGES.map((s) => ({ ...s, cards: cards.filter(s.match) }));
  const openValue = cards
    .filter((c) => c.kind === 'quote' && (c.stage === 'draft' || c.stage === 'sent'))
    .reduce((sum, c) => sum + (c.valueCents ?? 0), 0);
  const wonValue = cards
    .filter((c) => c.kind === 'quote' && c.stage === 'approved')
    .reduce((sum, c) => sum + (c.valueCents ?? 0), 0);

  return (
    <PageRoot>
      <PageHeader
        title="Pipeline"
        sub="Everything you've validated — quotes flowing into jobs."
      />
      <KpiGrid>
        <KpiCard label="In play" value={cards.length} />
        <KpiCard label="Out for approval" value={moneyShort(openValue)} tone="brand" />
        <KpiCard label="Won, to schedule" value={moneyShort(wonValue)} tone="success" />
        <KpiCard
          label="Ready to invoice"
          value={columns.find((c) => c.key === 'toinvoice')?.cards.length ?? 0}
        />
      </KpiGrid>

      <UnderlineTabs value={pipeline} onValueChange={setPipeline}>
        <UnderlineTabStrip tabs={PIPELINES} />
      </UnderlineTabs>

      {cards.length === 0 ? (
        <EmptyState
          icon={IconPipeline}
          title="Nothing in the pipeline"
          description="Approve a request into a quote or a job and it appears here."
        />
      ) : (
        <Toolbar>
          <div className="h-scroll w-full pb-2">
            <div className="flex min-w-max gap-3">
              {columns.map((col) => (
                <section
                  key={col.key}
                  className="w-[260px] shrink-0 rounded-[var(--radius)] border border-border bg-surface"
                >
                  <div className="flex items-center justify-between border-b border-border px-3 py-2">
                    <h2 className="text-sm font-semibold">{col.label}</h2>
                    <span className="tabular-nums text-xs text-muted-foreground">
                      {col.cards.length}
                    </span>
                  </div>
                  <ul className="space-y-2 p-2">
                    {col.cards.length === 0 ? (
                      <li className="py-3 text-center text-xs text-muted-foreground">
                        Empty
                      </li>
                    ) : (
                      col.cards.map((c) => (
                        <li key={`${c.kind}-${c.id}`}>
                          <button
                            type="button"
                            onClick={() =>
                              c.kind === 'quote'
                                ? navigate({
                                    to: '/quotes/$quoteId',
                                    params: { quoteId: c.id },
                                  })
                                : navigate({
                                    to: '/jobs/$jobId',
                                    params: { jobId: c.id },
                                  })
                            }
                            className="w-full rounded-[var(--radius-sm)] border border-border p-2 text-left hover:bg-surface-2"
                          >
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="font-mono text-xs text-muted-foreground">
                                {c.ref}
                              </span>
                              <StatusBadge tone={c.kind === 'quote' ? 'info' : 'brand'}>
                                {c.kind}
                              </StatusBadge>
                            </div>
                            <p className="mt-1 truncate text-sm font-medium">{c.title}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {c.customerName ?? '—'}
                            </p>
                            {c.valueCents !== null ? (
                              <p className="mt-1 text-xs font-medium tabular-nums">
                                {money(c.valueCents)}
                              </p>
                            ) : null}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </Toolbar>
      )}

      {/* A board that quietly showed the first 200 and called it the pipeline would be a
          silent cap — the one thing a board must never do. Say what is not on screen. */}
      {truncatedBy > 0 ? (
        <p className="text-xs text-muted-foreground">
          Showing the {LIST_LIMIT_MAX} most recent of each. {truncatedBy.toLocaleString()}{' '}
          older {truncatedBy === 1 ? 'item is' : 'items are'} not on this board — the{' '}
          <Link to="/quotes" className="font-semibold underline">
            quotes
          </Link>{' '}
          and{' '}
          <Link to="/jobs" className="font-semibold underline">
            jobs
          </Link>{' '}
          lists show everything.
        </p>
      ) : null}
    </PageRoot>
  );
}
